import {WriteButton} from '../../common/buttons/WriteActions';
import {ImageUploadStatus} from '../ImageUploadStatus';

import {OrderGuidDto} from '@mybake/ui/models/Order/IOrderGuid';
import {LocalizedDateField} from '../../common/dates/LocalizedDateField';
import {useWorkshopDates} from '../useWorkshopDates';
import {useEffect, useMemo, useRef, useState} from 'react';
import {Alert, Box, Snackbar, Stack, TextField, Typography} from '@mui/material';
import {DTO_STATE, IS_DELETED, ORDER_STAGES} from '@mybake/ui/common/dictionary';
import {compareObjects, getDateTime, hasStateNewOrDeleted} from '@mybake/ui/common/helpers';
import {IOrder} from '../../../models/Order/IOrder';
import {ISubOrder} from '../../../models/Order/ISubOrder';
import {orderAPI} from '../../../services/OrderServices';
import {recipeAPI} from '../../../services/RecipeServices';
import EditSubOrderHOC from './EditSubOrderHOC';
import {OrderValidator} from '../../../validators/OrderValidator';
import {imageAPI} from '../../../services/ImageService';
import {packageAPI} from "../../../services/PackageServices";
import {useAppTranslation} from "@mybake/ui/hooks/useAppTranslation";
import {useNavigate} from "react-router-dom";
import {decorationAPI} from '../../../services/DecorationService';
import {productTypesAPI} from '../../../services/ProductTypeServices';
import {OrderFormSection} from '../OrderFormSection';
import {roundMoney} from '../subOrderCost';
import {useCurrency} from '../../../hooks/useCurrency';
import {MoneyField} from '../../common/inputs/MoneyField';
import {normalizeOrderIds} from './normalizeOrderIds';
import {getApiErrorKind, getApiErrorMessage, getApiTraceId} from '../../../common/apiErrors';
import TraceIdFeedback from '../../feedback/TraceIdFeedback';
import {ContentSkeleton} from '../../common/loading/ContentSkeleton';
import {toSaveOrderGuidRequest} from '../../../mappers/orderGuid.mapper';
import {IRecipe} from '../../../models/Recipe/IRecipe';
import {IPackage} from '../../../models/Packages/IPackage';
import {IDecoration} from '../../../models/Decorations/IDecoration';
import {IProductType} from '../../../models/ProductTypes/IProductType';

interface Props {
    order: IOrder;
    onSaved?: () => void;
}

const EditOrderV2 = ({order, onSaved}: Props) => {
    const workshopDates = useWorkshopDates();
    const {t} = useAppTranslation();
    const {formatCurrency} = useCurrency();
    const navigate = useNavigate();
    const subOrdersReadOnly = order.stage === ORDER_STAGES.DONE;

    const {
        data: recipeDtos,
        error: getRecipesError,
        isLoading: getRecipesIsLoading,
        isSuccess: getRecipesIsSuccess
    } = recipeAPI.useGetRecipesForOrderV2Query();
    const {
        data: packageDtos,
        isError: packagesError,
        isLoading: packagesLoading,
        isSuccess: packagesSuccess
    } = packageAPI.useGetPackagesForOrderV2Query();
    const {data: decorationDtos} = decorationAPI.useGetDecorationsForOrderV2Query();
    const {data: productTypeDtos} = productTypesAPI.useGetProductTypesForOrderV2Query();
    const [saveOrder, {error: saveOrderError}] = orderAPI.useUpdateOrderV2Mutation();
    const recipes = useMemo<IRecipe[]>(() => (recipeDtos ?? []).map(recipe => ({...recipe, parts: undefined, recipeDescription: '', isDeleted: IS_DELETED.ACTIVE, state: DTO_STATE.DTO_UNCHANGED, createdAt: null, recipeFillings: [], fillings: recipe.fillings.map(filling => ({...filling, fillingDescription: '', isDeleted: IS_DELETED.ACTIVE, state: DTO_STATE.DTO_UNCHANGED, createdAt: null, price: Number(filling.proportionInfos[0]?.cost ?? 0), items: [], recipes: []}))})), [recipeDtos]);
    const packages = useMemo<IPackage[]>(() => (packageDtos ?? []).map(value => ({...value, id: value.id ?? '', state: DTO_STATE.DTO_UNCHANGED, productTypes: value.productTypes ?? [], stocks: []})), [packageDtos]);
    const decorations = useMemo<IDecoration[]>(() => (decorationDtos ?? []).map(value => ({...value, id: value.id ?? '', state: DTO_STATE.DTO_UNCHANGED, items: [], priceOptions: value.priceOptions.map(option => ({...option, state: DTO_STATE.DTO_UNCHANGED}))})), [decorationDtos]);
    const productTypes = useMemo<IProductType[]>(() => (productTypeDtos ?? []).map(value => ({...value, packages: [], recipes: [], state: DTO_STATE.DTO_UNCHANGED, priceTiers: value.priceTiers.map(tier => ({...tier, id: tier.id ?? undefined, state: DTO_STATE.DTO_UNCHANGED}))})), [productTypeDtos]);
    const [editedOrder, setEditedOrder] = useState<IOrder>({...order});
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const customerData = order.customer!.instagram || order.customer!.faceBook || order.customer!.name;
    const [prepaymentDate, setPrepaymentDate] = useState<string>();
    const [prepaymentBalance, setPrepaymentBalance] = useState<number>(0);
    const inputBalanceRef = useRef<HTMLInputElement>(null);
    const validator = useMemo(() => OrderValidator.getInstance(editedOrder, recipes || []), [editedOrder, recipes]);
    const {totalCost, totalPrice} = useMemo(() => {
        const activeSubOrders = (editedOrder.subOrders ?? []).filter(subOrder => subOrder.state !== DTO_STATE.DTO_DELETED);

        return {
            totalCost: roundMoney(activeSubOrders.reduce((sum, subOrder) => sum + (Number(subOrder.productCost) || 0), 0)),
            totalPrice: roundMoney(activeSubOrders.reduce((sum, subOrder) => sum + (Number(subOrder.price) || 0), 0)),
        };
    }, [editedOrder.subOrders]);
    const forPayment = totalPrice - (order?.prepayment?.prepayment ?? 0) - prepaymentBalance;

    const datePickUp = getDateTime(order.pickUpAt);
    const [changePickUpDate, setChangePickUpDate] = useState<boolean>(false);
    const [changePrepayment, setChangePrepayment] = useState<boolean>(false);

    const [newPickUpDate, setNewPickUpDate] = useState<string>('');
    const [fileMap, setFileMap] = useState<Map<string, File>>(new Map());
    const [serverToast, setServerToast] = useState('');
    const [traceId, setTraceId] = useState('');
    const [savedCheckpoint, setSavedCheckpoint] = useState<OrderGuidDto | null>(null);
    const [saveOrderIsSuccess, setSaveOrderIsSuccess] = useState(false);
    const [saveImages, {error: saveImagesError, isLoading: imagesUploading}] = imageAPI.useUploadImagesV2Mutation();

    const handlePrepaymentBalance = (value: number | undefined) => {
        const prepayment = value ?? 0;
        setPrepaymentBalance(prepayment);
        const prepaymentState =
            prepayment === order.prepayment?.prepayment && (prepaymentDate || order.prepayment.prepaymentAt) === order.prepayment.prepaymentAt
                ? DTO_STATE.DTO_UNCHANGED
                : DTO_STATE.DTO_NEW;

        setEditedOrder((prev) => {
            return {
                ...prev,
                prepayment: {
                    ...order.prepayment!,
                    prepaymentAt: prepaymentDate || order.prepayment?.prepaymentAt,
                    prepayment: prepayment,
                    state: order.prepayment?.prepayment === prepayment ? DTO_STATE.DTO_UNCHANGED : DTO_STATE.DTO_NEW,
                    orderId: prepaymentState === DTO_STATE.DTO_NEW ? editedOrder.id : 0,
                },
            };
        });
    };
    const handlePickUpDateTime = (e: any) => {
        processDate(e);
    };
    const handlePrepaymentDate = (e: any) => {
        const exDateTime = new RegExp('(?<year>\\d{4})-(?<month>\\d{1,2})-(?<day>\\d{1,2})');
        if (exDateTime.test(e.target.value)) {
            setPrepaymentDate(e.target.value);
            setEditedOrder((prev) => {
                return {
                    ...prev,
                    prepayment: {
                        ...order.prepayment!,
                        prepayment: prepaymentBalance,
                        prepaymentAt: e.target.value,
                        state: DTO_STATE.DTO_NEW,
                        orderId: editedOrder.id,
                    },
                };
            });
        } else {
            setPrepaymentDate('');
            setEditedOrder((prev) => {
                return {
                    ...prev,
                    prepayment: {
                        ...order.prepayment!,
                        prepayment: prepaymentBalance,
                        prepaymentAt: order.prepayment?.prepaymentAt || '',
                        state: order.prepayment?.prepayment === prepaymentBalance ? DTO_STATE.DTO_UNCHANGED : DTO_STATE.DTO_NEW,
                    },
                };
            });
        }
    };

    const processDate = (e: any) => {
        const exDateTime = new RegExp('(?<year>\\d{4})-(?<month>\\d{1,2})-(?<day>\\d{1,2})');
        if (exDateTime.test(e.target.value)) {
            setEditedOrder((prev) => {
                return {
                    ...prev,
                    pickUpAt: e.target.value,
                };
            });
            setNewPickUpDate(e.target.value);
        }
    };

    let init: ISubOrder[] = [];
    order.subOrders?.forEach((subOrder) => {
        init.push({...subOrder});
    });

    const handleSaveOrder = async () => {
        const finalOrder = normalizeOrderIds({...editedOrder, price: totalPrice, cost: totalCost, state: DTO_STATE.DTO_MODIFIED});
        setIsSaveDisabled(true);
        try {
            const saved = savedCheckpoint ?? await saveOrder({id: String(order.id), body: toSaveOrderGuidRequest(finalOrder)}).unwrap();
            setSavedCheckpoint(saved);
            await saveImages({saved, draft: finalOrder, files: fileMap}).unwrap();
            setSaveOrderIsSuccess(true);
            if (onSaved) onSaved();
            else navigate("..", {relative: "path"});
        } catch (error) {
            setIsSaveDisabled(false);
            if (getApiErrorKind(error) === 'server') {
                setServerToast(getApiErrorMessage(error, t));
                setTraceId(getApiTraceId(error));
            }
        }
    };

    const handleNewFiles = (files: Map<string, File>) => {
        //const fileNames = editedOrder.subOrders?.flatMap((x) => x.images?.map((i) => i.fileName)) || [];
        // const fileNames = new Set(editedOrder.subOrders?.flatMap((sub) => sub.images?.map((img) => img.fileName) || []) ?? []);
        // files.forEach((file, key) => {
        //     fileMap.set(key, file);
        // });

        //
        // Array.from(fileMap.keys()).forEach((key) => {
        //     if (!fileNames.has(key)) {
        //         fileMap.delete(key);
        //     }
        // });

        setFileMap((prevFiles) => {
            const updated = new Map(prevFiles);
            files.forEach((file, key) => {
                updated.set(key, file);
            });
            const fileNameSet = new Set(editedOrder.subOrders?.flatMap((sub) => sub.images?.map((img) => img.fileName) || []) ?? []);
            Array.from(updated.keys()).forEach((key) => {
                if (!fileNameSet.has(key)) {
                    updated.delete(key);
                }
            });

            return updated;
        });
    };

    const setOrderDescription = (e: any) => {
        setEditedOrder((prev) => {
            return {
                ...prev,
                description: e.target.value,
            };
        });
    };

    const saveSubOrderChanges = (subOrder: ISubOrder) => {
        let subOrders = editedOrder.subOrders?.map((el) => {
            if (el.id === subOrder.id) {
                if (subOrder.state !== DTO_STATE.DTO_NEW) {
                    const isHasChanged = !compareObjects(
                        subOrder,
                        order.subOrders?.find((e) => e.id === subOrder.id),
                    );
                    if (isHasChanged || hasStateNewOrDeleted(subOrder.images || [])) {
                        subOrder.state = DTO_STATE.DTO_MODIFIED;
                    } else {
                        subOrder.state = DTO_STATE.DTO_UNCHANGED;
                    }
                }
                return subOrder;
            }
            return el;
        });
        setEditedOrder((prev) => {
            return {...prev, subOrders: [...(subOrders || [])]};
        });
    };

    useEffect(() => {
        const isHasChanged = editedOrder.subOrders?.length === order.subOrders?.length ? !compareObjects(editedOrder, order) : true;
        const hasNewState =
            hasStateNewOrDeleted(editedOrder.subOrders || []) ||
            (editedOrder.prepayment != null && hasStateNewOrDeleted([editedOrder.prepayment])) ||
            hasStateNewOrDeleted(editedOrder?.subOrders?.flatMap((x) => x.images) || []) ||
            hasStateNewOrDeleted(editedOrder?.subOrders?.flatMap((x) => x.packages) || []
            ) ||
            hasStateNewOrDeleted(editedOrder?.subOrders?.flatMap((x) => x.decorations) || []);
        const hasDiff = isHasChanged || hasNewState;
        const hasInvalidDecorations = editedOrder.subOrders
            ?.flatMap(x => x.decorations || [])
            .some(x => x.state !== DTO_STATE.DTO_DELETED && (!x.decorationId || x.quantity <= 0)) ?? false;
        const validOrder = validator.validate(true) && !hasInvalidDecorations;

        if (!hasDiff && validOrder) {
            setIsSaveDisabled(true);
        }
        if (hasDiff && validOrder) {
            setIsSaveDisabled(false);
        }
        if (hasDiff && !validOrder) {
            setIsSaveDisabled(true);
        }
    }, [editedOrder]);

    const deleteSubOrder = (subOrderId: number | string) => {
        let subOrders: ISubOrder[] = [...(editedOrder.subOrders || [])];
        if (subOrderId.toString().includes('-')) {
            subOrders = subOrders?.filter((e) => e.id.toString() !== subOrderId.toString());
        } else {
            subOrders = subOrders.map((e) => {
                if (e.id === subOrderId) {
                    return {...e, state: DTO_STATE.DTO_DELETED};
                }
                return e;
            });
        }

        setEditedOrder((prev) => {
            return {...prev, subOrders: [...subOrders]};
        });
    };

    const restoreSubOrder = (subOrderId: number | string) => {
        setEditedOrder((prev) => ({
            ...prev,
            subOrders: prev.subOrders?.map((subOrder) =>
                subOrder.id === subOrderId
                    ? {...subOrder, state: DTO_STATE.DTO_UNCHANGED}
                    : subOrder
            ),
        }));
    };

    const addNewSubOrder = () => {
        setEditedOrder((prev) => {
            return {
                ...prev,
                subOrders: [
                    ...(prev.subOrders || []),
                    {
                        id: crypto.randomUUID(),
                        recipeName: '',
                        recipeId: 0,
                        description: '',
                        price: 0,
                        productCost: 0,
                        count: 1,
                        recipeCounter: 0,
                        orderId: order.id,
                        isDeleted: IS_DELETED.ACTIVE,
                        state: DTO_STATE.DTO_NEW,
                        fillingId: 0,
                        images: [],
                        packages: [],
                        decorations: [],
                    },
                ],
            };
        });
    };

    useEffect(() => {
        if (!changePickUpDate) {
            setEditedOrder((prev) => {
                return {
                    ...prev,
                    pickUpAt: order.pickUpAt,
                };
            });
            setNewPickUpDate('');
        }
    }, [changePickUpDate]);

    useEffect(() => {
        if (!changePrepayment) {
            setEditedOrder((prev) => {
                return {
                    ...prev,
                    prepayment: {...order.prepayment!},
                };
            });
            setPrepaymentBalance(0);
            setPrepaymentDate('');
        }
    }, [changePrepayment]);

    if (getRecipesIsLoading || packagesLoading) return <ContentSkeleton variant="form"/>;

    return (
        <Box sx={{width: '100%'}}>
            {saveOrderError && getApiErrorKind(saveOrderError) !== 'server' && <Alert severity="error" sx={{mb: 2}}>{getApiErrorMessage(saveOrderError, t)}</Alert>}
            {saveImagesError && <Alert severity="error" sx={{mb: 2}}>{getApiErrorMessage(saveImagesError, t)}</Alert>}
            <Stack spacing={2}>
                        {savedCheckpoint && !saveOrderIsSuccess && <ImageUploadStatus orderId={savedCheckpoint.id} uploading={imagesUploading}/>}
                        <Box component="fieldset" disabled={Boolean(savedCheckpoint)} sx={{border: 0, p: 0, m: 0, minWidth: 0}}>
                        <Stack spacing={2}>
                <OrderFormSection title={`${t('orders:order_number')} ${order.orderNumber || ''}`}>
                    <TextField
                        required
                        fullWidth
                        multiline
                        minRows={3}
                        id="description"
                        label={t('orders:order_description')}
                        value={editedOrder.description}
                        onChange={setOrderDescription}
                    />
                </OrderFormSection>

                <OrderFormSection title={t('customers:customer')}>
                    <Box sx={{p: 1.25, borderRadius: 2, bgcolor: 'action.hover'}}>
                        <Typography fontWeight={700}>{order.customer?.name || customerData || '—'}</Typography>
                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={{xs: 0.25, sm: 2}}>
                            {order.customer?.phoneNumber && <Typography variant="body2" color="text.secondary">{order.customer.phoneNumber}</Typography>}
                            {order.customer?.email && <Typography variant="body2" color="text.secondary">{order.customer.email}</Typography>}
                        </Stack>
                    </Box>
                </OrderFormSection>

                <OrderFormSection title={t('finance:prepayment')} action={
                    <WriteButton size="small" variant="outlined" onClick={() => setChangePrepayment(!changePrepayment)}>
                        {changePrepayment ? t('common:cancel') : t('finance:change_prepayment')}
                    </WriteButton>
                }>
                    <Stack spacing={1.25}>
                        <Typography fontWeight={700}>
                            {order.stage === ORDER_STAGES.PREPAID
                                ? `${t('finance:received')} ${formatCurrency(order.prepayment?.prepayment ?? 0)} ${order.prepayment?.prepaymentAt}`
                                : t('finance:waiting')}
                        </Typography>
                        {changePrepayment && (
                            <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.25}>
                                <MoneyField
                                    inputRef={inputBalanceRef}
                                    required
                                    fullWidth
                                    id="prepaymentBalance"
                                    label={t('finance:prepayment_balance')}
                                    value={prepaymentBalance}
                                    onValueChange={handlePrepaymentBalance}
                                    onFocus={() => inputBalanceRef.current && inputBalanceRef.current.select()}
                                />
                                <LocalizedDateField
                                    required
                                    fullWidth
                                    id="prepaymentAt"
                                    label={t('dates:prepayment_at')}
                                    type="date"
                                    onChange={handlePrepaymentDate}
                                    InputLabelProps={{shrink: true}}
                                />
                            </Stack>
                        )}
                    </Stack>
                </OrderFormSection>

                <OrderFormSection
                    title={t('orders:sub_orders')}
                    action={!subOrdersReadOnly ? <WriteButton size="small" variant="contained" onClick={addNewSubOrder}>{t('orders:add_suborder')}</WriteButton> : undefined}
                >
                    <Stack spacing={1.25}>
                        {subOrdersReadOnly && <Typography variant="body2" color="text.secondary">{t('orders:done_order_edit_forbidden')}</Typography>}
                        {editedOrder.subOrders?.map((subOrder) => (
                            <EditSubOrderHOC
                                key={`hoc_${subOrder.id}`}
                                existSubOrder={order.subOrders?.find((el) => el.id === subOrder.id)}
                                editedSubOrder={subOrder}
                                recipes={recipes}
                                packages={packages}
                                decorations={decorations}
                                productTypes={productTypes}
                                deleteSubOrder={deleteSubOrder}
                                restoreSubOrder={restoreSubOrder}
                                saveSubOrderChanges={saveSubOrderChanges}
                                handleNewFiles={handleNewFiles}
                                readOnly={subOrdersReadOnly}
                            />
                        ))}
                    </Stack>
                </OrderFormSection>

                <OrderFormSection
                    title={t('dates:pick_up_date')}
                    action={<WriteButton size="small" variant="outlined" onClick={() => setChangePickUpDate(!changePickUpDate)}>{changePickUpDate ? t('common:cancel') : t('dates:change_pickup_date')}</WriteButton>}
                >
                    <Stack spacing={1.25}>
                        <Typography fontWeight={700}>{datePickUp?.date} · {datePickUp?.time}</Typography>
                        {changePickUpDate && (
                            <LocalizedDateField
                                showHolidayLegend
                                workshopDates={workshopDates}
                                required
                                fullWidth
                                id="pickUpDate"
                                label={t('dates:pick_up_date')}
                                type="datetime-local"
                                onChange={handlePickUpDateTime}
                                color={!newPickUpDate ? 'error' : 'success'}
                                InputLabelProps={{shrink: true}}
                            />
                        )}
                    </Stack>
                </OrderFormSection>

                <OrderFormSection title={t('finance:total_order_price')}>
                    <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(3, 1fr)'}, gap: 1.5}}>
                        <Box><Typography variant="caption" color="text.secondary">{t('finance:total_order_price')}</Typography><Typography fontWeight={700}>{formatCurrency(totalPrice)}</Typography></Box>
                        <Box><Typography variant="caption" color="text.secondary">{t('finance:total_order_cost')}</Typography><Typography fontWeight={700}>{formatCurrency(totalCost)}</Typography></Box>
                        <Box><Typography variant="caption" color="text.secondary">{t('finance:rest_for_payment')}</Typography><Typography fontWeight={700}>{formatCurrency(forPayment)}</Typography></Box>
                    </Box>
                </OrderFormSection>

                        </Stack>
                        </Box>
                <WriteButton variant="contained" fullWidth onClick={handleSaveOrder} disabled={isSaveDisabled}>
                    {t('orders:save_order')}
                </WriteButton>
            </Stack>
            <Snackbar open={!!serverToast} autoHideDuration={6000} onClose={() => setServerToast('')}><Alert severity="error" onClose={() => setServerToast('')}>{serverToast}<TraceIdFeedback traceId={traceId}/></Alert></Snackbar>
        </Box>
    );
};

export default EditOrderV2;
